import isLodashEmpty from "lodash-es/isEmpty";

export function hasProperty(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * This function will gather all the classes either provided to the component through props
 * or the additional classes required at the component level
 * @param {*} props props passed to the component.
 *    props should be an object containing a property 'className'
 * @param {...string} extraClasses classes other than the passed through props.
 *    these should be a comma separated list of class names (string)
 */
export function gatherClasses(props, ...extraClasses) {
  const classes = [];

  if (props && props.className) {
    classes.push(props.className);
  }
  if (extraClasses && extraClasses.length > 0) {
    extraClasses.forEach(className => {
      if (className && className !== "") {
        classes.push(className);
      }
    });
  }

  return classes.length > 0 ? classes.join(" ") : null;
}

/**
 * There are couple of situations where the property might be provided or not
 * In such case this will be a common location to collect such conditional properties
 * @param {*} props
 *    props those are passes to the component
 * @param {...string} conditionalProps
 *    names of the props the component is interested in
 */
export function gatherConditionalProps(props, ...conditionalProps) {
  const properties = {};
  if (props && conditionalProps.length > 0) {
    conditionalProps.forEach(prop => {
      if (hasProperty(props, prop)) {
        properties[prop] = props[prop];
      }
    });
  }

  return properties;
}

/**
 * This utility function will gather given conditional styles from props
 * and if at least one style is found, then will return style object
 */
export function gatherStyles(props, ...conditionalStyles) {
  const styles = {};
  if (props && conditionalStyles.length > 0) {
    conditionalStyles.forEach(prop => {
      if (hasProperty(props, prop)) {
        styles[prop] = props[prop];
      }
    });
  }
  return Object.keys(styles).length === 0
    ? null
    : {
        style: styles
      };
}

/**
 * This utility function should be used to see if the given prop is actually a function or not.
 * @param {any} func
 *    the prop to be tested if it is a function
 */
export function isFunction(func) {
  return func && typeof func === "function";
}

/**
 * This utility function should be used to see if the given prop is actually a string or not.
 * @param {any} str
 *    the prop to be tested if it is a string
 */
export function isString(str) {
  return str && typeof str === "string";
}

/**
 * Utility function to check if given item is real javascript object or not
 *
 * @param {any} obj
 */
export function isObject(obj) {
  return obj && typeof obj === "object" && !isFunction(obj);
}

/**
 * A utility function to generate dynamic id
 */
export function generateId(prefix = "", suffix = "") {
  const length = 8;

  const _getRandomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const parts = Date.now().toString().split("").reverse();
  let id = "";

  for (let i = 0; i < length; ++i) {
    const index = _getRandomInt(0, parts.length - 1);
    id += parts[index];
  }

  return prefix + id + suffix;
}

/**
 * This function will calculate the percentage out of given total and value
 */
export function getPercentageValue(total = 100, value = 0) {
  return (Number(value) * 100) / Number(total);
}

/**
 * A utility function for conditional required prop types
 */
export function requiredIf(type, condition) {
  return function cb(props, propName, componentName) {
    if (typeof type !== "function") {
      return new Error(
        `Invalid react-required-if prop type supplied to ${componentName}. Validation failed.`
      );
    }

    if (typeof condition !== "function") {
      return new Error(
        `Invalid react-required-if condition supplied to ${componentName}. Validation failed.`
      );
    }

    const test = condition(props) ? type.isRequired : type;
    // eslint-disable-next-line prefer-rest-params
    return test.apply(this, arguments);
  };
}

export function isDescendant(parent, child) {
  let node = child.parentNode;
  while (node != null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

export function isEmpty(value) {
  const type = typeof value;

  if (type === "string" || type === "boolean" || type === "number") {
    return !value || value === "";
  }

  if (value === null || typeof value === "undefined") {
    return true;
  }

  if (!(value instanceof Date)) {
    return isLodashEmpty(value);
  }

  return !value;
}

export function debounce(func, wait, immediate) {
  let timeout;

  const debounced = () => {
    const context = this;
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };

  debounced.cancel = function cb() {
    if (timeout !== null && typeof timeout === "number") {
      clearTimeout(timeout);
    }
  };

  return debounced;
}

export function isMobileBrowser() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export function getValueInPx(value) {
  return String(value).endsWith("px") ? value : `${value}px`;
}

export function isSafariBrowser() {
  return (
    navigator.vendor.match(/apple/i) &&
    !navigator.userAgent.match(/crios/i) &&
    !navigator.userAgent.match(/fxios/i)
  );
}
